/**
 * 订单外部状态工具
 *
 * 外部可查状态由后端直接返回（后端已完成内部状态 → 外部状态的归并），
 * 前端只负责「外部状态码 → 本地化文案」的展示，不实现任何归并算法、不引入内部状态枚举。
 *
 * 状态码与后端 OverallStatusExternalConverter 归并出的 11 个客户可见外部状态一致；
 * 下拉选项优先由后端 portalExternalStatuses 下发，本常量仅作为兜底与文案完整性校验来源。
 * 后端无法归并时返回 null（不再有 UNKNOWN 哨兵值），前端统一展示 "-"，与 pickupHubName 同一约定；
 * 若后端返回未收录的状态码，label 函数兜底返回原始 code，不会报错。
 */

/**
 * 外部可查状态码（后端 11 个外部状态，每个都可作为筛选条件）。
 *
 * 数组顺序即下拉展示顺序，与后端 ExternalOrderStatusEnum 声明顺序保持一致。
 */
export const EXTERNAL_ORDER_STATUS_CODES = [
    'CREATED',
    'CANCELLED',
    'ARRIVED_ORIGIN_HUB',
    'PICKUP_FAILED',
    'IN_TRANSIT',
    'OUT_FOR_DELIVERY',
    'DELIVERED',
    'DELIVERY_FAILED',
    'RETURN_IN_PROGRESS',
    'RETURNED',
    'MISSING',
] as const;

export type ExternalOrderStatus = (typeof EXTERNAL_ORDER_STATUS_CODES)[number];

/** 翻译函数最小签名，兼容 react-i18next 的 t（第二参为默认值） */
type TFunc = (key: string, defaultValue?: string) => string;

/**
 * 外部状态码 → 本地化文案。
 *
 * 命中 i18n key `orderQuery.status.<code>` 时返回对应文案；
 * 未命中（后端新增了前端尚未收录的状态）时兜底返回原始 code，避免暴露 i18n key 字面量；
 * 空值（后端无法归并，返回 null）时返回 '-'——此时调用方应直接渲染文本，不要套 Tag，
 * 空状态没有"状态标签"可言，与其它无值字段的 '-' 保持一致的视觉。
 */
export const getExternalStatusLabel = (code: string | null | undefined, t: TFunc): string => {
    if (!code) return '-';
    return t(`orderQuery.status.${code}`, code);
};
